// src/components/onboarding/PdfCanvasViewer.tsx
//
// Renders a PDF (given as a Blob or object-URL string) to stacked <canvas>
// elements using pdf.js. Unlike an <iframe src={pdfBlobUrl}>, this works
// reliably on mobile browsers (iOS Safari / Android Chrome), which refuse to
// render PDF blobs inside an iframe.
//
// - Responsive: every page is rendered at the container's current width and
//   re-rendered when the container is resized (ResizeObserver).
// - Crisp: rendering is devicePixelRatio-aware (canvas backing store is scaled
//   up by the DPR while the CSS size stays at the container width).
// - Safe: the loaded PDFDocumentProxy is destroyed and any in-flight render
//   tasks are cancelled on unmount or whenever the source blob changes.

import React, { useEffect, useRef, useState } from 'react';
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';

// Load the pdf.js worker from /public (copied there from
// node_modules/pdfjs-dist/build/pdf.worker.min.js). Using PUBLIC_URL makes it
// resolve correctly under any CRA homepage / deploy sub-path, and it is served
// as a static asset so no bundler worker wiring is required.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

interface PdfCanvasViewerProps {
    /** The PDF source. Either a Blob or an already-created object URL. */
    source: Blob | string;
    /** Accessible label / title for the viewer. */
    title?: string;
    /** Max height of the scrollable viewport (px). Defaults to 560. */
    maxHeight?: number;
    className?: string;
}

type Status = 'loading' | 'ready' | 'error';

const PdfCanvasViewer: React.FC<PdfCanvasViewerProps> = ({
    source,
    title = 'Document preview',
    maxHeight = 560,
    className = '',
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const canvasRefs = useRef<Array<HTMLCanvasElement | null>>([]);
    const pdfDocRef = useRef<PDFDocumentProxy | null>(null);
    const renderTasksRef = useRef<RenderTask[]>([]);

    const [status, setStatus] = useState<Status>('loading');
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [numPages, setNumPages] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);
    // Bumped to force a reload (retry button).
    const [reloadKey, setReloadKey] = useState(0);

    // Cancel and clear any in-flight render tasks.
    const cancelRenders = () => {
        renderTasksRef.current.forEach((t) => {
            try {
                t.cancel();
            } catch {
                /* ignore */
            }
        });
        renderTasksRef.current = [];
    };

    // Track the container width so pages can be rendered to fit it.
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        setContainerWidth(el.clientWidth);
        let frame = 0;
        const ro = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width ?? el.clientWidth;
            // Coalesce bursts of resize callbacks into a single state update.
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => setContainerWidth(Math.round(width)));
        });
        ro.observe(el);
        return () => {
            cancelAnimationFrame(frame);
            ro.disconnect();
        };
    }, []);

    // Load (or reload) the PDF document whenever the source changes.
    useEffect(() => {
        let cancelled = false;
        setStatus('loading');
        setErrorMsg(null);
        setNumPages(0);

        const load = async () => {
            try {
                let data: ArrayBuffer;
                if (typeof source === 'string') {
                    const res = await fetch(source);
                    data = await res.arrayBuffer();
                } else {
                    data = await source.arrayBuffer();
                }
                if (cancelled) return;
                // Pass a fresh copy — pdf.js may transfer/detach the buffer.
                const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(data) });
                const pdf = await loadingTask.promise;
                if (cancelled) {
                    pdf.destroy();
                    return;
                }
                pdfDocRef.current = pdf;
                canvasRefs.current = new Array(pdf.numPages).fill(null);
                setNumPages(pdf.numPages);
                setStatus('ready');
            } catch (err) {
                if (cancelled) return;
                setErrorMsg('We could not display this document.');
                setStatus('error');
            }
        };

        void load();

        return () => {
            cancelled = true;
            cancelRenders();
            if (pdfDocRef.current) {
                pdfDocRef.current.destroy();
                pdfDocRef.current = null;
            }
        };
    }, [source, reloadKey]);

    // Render every page to its canvas at the current container width.
    // Re-runs when the document loads or the container is resized.
    useEffect(() => {
        const pdf = pdfDocRef.current;
        if (status !== 'ready' || !pdf || numPages === 0 || containerWidth === 0) return;

        let cancelled = false;
        cancelRenders();

        const renderAll = async () => {
            const dpr = window.devicePixelRatio || 1;
            for (let pageNum = 1; pageNum <= numPages; pageNum++) {
                if (cancelled) return;
                let page;
                try {
                    page = await pdf.getPage(pageNum);
                } catch {
                    return;
                }
                if (cancelled) return;
                const canvas = canvasRefs.current[pageNum - 1];
                if (!canvas) continue;

                // Scale so the page fits the container width, then multiply by DPR
                // for a crisp backing store.
                const baseViewport = page.getViewport({ scale: 1 });
                const cssScale = containerWidth / baseViewport.width;
                const viewport = page.getViewport({ scale: cssScale * dpr });

                const ctx = canvas.getContext('2d');
                if (!ctx) continue;

                canvas.width = Math.floor(viewport.width);
                canvas.height = Math.floor(viewport.height);
                canvas.style.width = '100%';
                canvas.style.height = 'auto';

                const task = page.render({ canvasContext: ctx, viewport });
                renderTasksRef.current.push(task);
                try {
                    await task.promise;
                } catch {
                    // RenderingCancelledException on resize/unmount — ignore.
                    return;
                }
            }
        };

        void renderAll();

        return () => {
            cancelled = true;
            cancelRenders();
        };
    }, [status, numPages, containerWidth]);

    return (
        <div
            ref={containerRef}
            className={`w-full overflow-y-auto overflow-x-hidden rounded-2xl border border-gray-200 bg-gray-100 ${className}`}
            style={{ maxHeight }}
            aria-label={title}
            role="document"
        >
            {status === 'loading' && (
                <div className="flex items-center justify-center gap-2 text-gray-400 py-16">
                    <Loader2 className="w-5 h-5 animate-spin" /> Loading document…
                </div>
            )}

            {status === 'error' && (
                <div className="p-6 text-sm text-brand flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                    <div className="space-y-2">
                        <span>{errorMsg}</span>
                        <button
                            type="button"
                            onClick={() => setReloadKey((k) => k + 1)}
                            className="flex items-center gap-1.5 text-sm font-bold text-brand hover:underline"
                        >
                            <RefreshCw className="w-3.5 h-3.5" /> Try again
                        </button>
                    </div>
                </div>
            )}

            {/* Stacked page canvases. Kept mounted (even while loading) so the
                ref callbacks are available by the time rendering starts. */}
            <div className={`flex flex-col items-center gap-3 p-2 sm:p-3 ${status === 'ready' ? '' : 'hidden'}`}>
                {Array.from({ length: numPages }).map((_, i) => (
                    <canvas
                        key={i}
                        ref={(el) => {
                            canvasRefs.current[i] = el;
                        }}
                        className="w-full max-w-full shadow-sm bg-white"
                    />
                ))}
            </div>
        </div>
    );
};

export default PdfCanvasViewer;
